import { ICampaignStatusToggle } from "./campaign-status-toggle.interface";
import { IToggleResult } from "../../review-toggle/itoggle-result.interface";
import { ApprovalStatus } from "../../approval-status/approval-status.type";
import {
  APPROVED_PAGE_STATUS,
  DRAFT_PAGE_STATUS,
  IN_PROGRESS_PAGE_STATUS,
  READY_FOR_APPROVAL_PAGE_STATUS,
  READY_FOR_CREATIVE_REVIEW,
  WORK_CAN_START,
} from "../../../utils/constants";

export const getPageStatusToggleValues = (
  context: ICampaignStatusToggle
): IToggleResult<ApprovalStatus> => {
  switch (context.pageType) {
    case "campaign-briefs":
      return getCampaignBriefDropDownValues(context);
    case "execution-briefs":
      return getExecutionBriefDropDownValues(context);
    case "work-orders":
      return getWorkOrderDropDownValues(context);
    default:
      return getDefaultDropDownValues();
  }
};

function getCampaignBriefDropDownValues(
  context: ICampaignStatusToggle
): IToggleResult<ApprovalStatus> {
  switch (context.pageStatus) {
    case "in_progress":
      return {
        disabled: noWritePermissions(context),
        dropdownValues: [IN_PROGRESS_PAGE_STATUS, DRAFT_PAGE_STATUS],
      };
    case "approved":
      return {
        disabled: true,
        dropdownValues: [APPROVED_PAGE_STATUS, DRAFT_PAGE_STATUS],
      };
    case "draft":
      return {
        disabled: noWritePermissionsOrReviewers(context),
        dropdownValues: [DRAFT_PAGE_STATUS, READY_FOR_APPROVAL_PAGE_STATUS],
      };
    default:
      return getDefaultDropDownValues();
  }
}

function getExecutionBriefDropDownValues(
  context: ICampaignStatusToggle
): IToggleResult<ApprovalStatus> {
  switch (context.pageStatus) {
    case "in_progress":
      return {
        disabled: noWritePermissions(context),
        dropdownValues: [IN_PROGRESS_PAGE_STATUS, DRAFT_PAGE_STATUS],
      };
    case "approved":
      return {
        disabled: true,
        dropdownValues: [APPROVED_PAGE_STATUS, DRAFT_PAGE_STATUS],
      };
    case "draft":
      return getExecutionBriefDraftDropDownValues(context);
    default:
      return getDefaultDropDownValues();
  }
}

function getWorkOrderDropDownValues(context: ICampaignStatusToggle): IToggleResult<ApprovalStatus> {
  switch (context.pageStatus) {
    case "work_can_start":
      return getWorkOrderInProgressDropDownValues(context);
    case "draft":
      return getWorkOrderDraftDropDownValues(context);
    case "ready_for_creative_review":
      return {
        disabled: noWritePermissions(context),
        dropdownValues: [READY_FOR_CREATIVE_REVIEW, DRAFT_PAGE_STATUS, WORK_CAN_START],
      };
    case "approved":
      return {
        disabled: true,
        dropdownValues: [APPROVED_PAGE_STATUS],
      };
    default:
      return getDefaultDropDownValues();
  }
}

function getWorkOrderInProgressDropDownValues(
  context: ICampaignStatusToggle
): IToggleResult<ApprovalStatus> {
  if (hasReviewers(context)) {
    return {
      disabled: noWritePermissions(context),
      dropdownValues: [WORK_CAN_START, DRAFT_PAGE_STATUS, READY_FOR_CREATIVE_REVIEW],
    };
  }

  return {
    disabled: noWritePermissions(context),
    dropdownValues: [WORK_CAN_START, DRAFT_PAGE_STATUS],
  };
}

function getWorkOrderDraftDropDownValues(
  context: ICampaignStatusToggle
): IToggleResult<ApprovalStatus> {
  if (context.parentPageStatus === "approved") {
    return {
      disabled: noWritePermissions(context),
      dropdownValues: [DRAFT_PAGE_STATUS, WORK_CAN_START],
    };
  }

  return {
    disabled: true,
    dropdownValues: [DRAFT_PAGE_STATUS],
  };
}

function getExecutionBriefDraftDropDownValues(
  context: ICampaignStatusToggle
): IToggleResult<ApprovalStatus> {
  if (context.parentPageStatus === "approved") {
    return {
      disabled: noWritePermissionsOrReviewers(context),
      dropdownValues: [DRAFT_PAGE_STATUS, READY_FOR_APPROVAL_PAGE_STATUS],
    };
  }

  return {
    disabled: true,
    dropdownValues: [DRAFT_PAGE_STATUS],
  };
}

const noWritePermissions = (context: ICampaignStatusToggle): boolean => !context.hasWritePermission;

const noWritePermissionsOrReviewers = (context: ICampaignStatusToggle): boolean =>
  noWritePermissions(context) || !context.reviewStages.length;

const hasReviewers = (context: ICampaignStatusToggle): boolean =>
  Boolean(context.reviewStages.length);

function getDefaultDropDownValues(): IToggleResult<ApprovalStatus> {
  return {
    disabled: true,
    dropdownValues: [DRAFT_PAGE_STATUS],
  };
}
